import { existsSync } from "node:fs";
import * as fs from "node:fs/promises";
import * as path from "node:path";
import { detect } from "@antfu/ni";
import { PRISMIC_CONFIG_FILENAME } from "../../constants/PRISMIC_CONFIG_FILENAME.js";
import { SLICEMACHINE_CONFIG_FILENAME } from "../../constants/SLICEMACHINE_CONFIG_FILENAME.js";
import { TS_CONFIG_FILENAME } from "../../constants/TS_CONFIG_FILENAME.js";
import { PrismicError, InternalError } from "../../errors.js";
import { assertPluginsInitialized } from "../../lib/assertPluginsInitialized.js";
import { decodePrismicConfig } from "../../lib/decodePrismicConfig.js";
import { format } from "../../lib/format.js";
import { installDependencies } from "../../lib/installDependencies.js";
import { locateFileUpward } from "../../lib/locateFileUpward.js";
import { BaseManager } from "../BaseManager.js";
class ProjectManager extends BaseManager {
  _cachedRoot;
  _cachedPrismicConfigPath;
  _cachedPrismicConfig;
  async getPrismicConfigPath(args) {
    if (this._cachedPrismicConfigPath && !args?.ignoreCache) {
      return this._cachedPrismicConfigPath;
    }
    try {
      this._cachedPrismicConfigPath = await locateFileUpward(PRISMIC_CONFIG_FILENAME, { startDir: this.cwd });
    } catch {
      throw new Error(`Could not find a ${PRISMIC_CONFIG_FILENAME} file. Please create a config file at the root of your project.`);
    }
    return this._cachedPrismicConfigPath;
  }
  async getRoot(args) {
    if (this._cachedRoot && !args?.ignoreCache) {
      return this._cachedRoot;
    }
    const prismicConfigFilePath = await this.getPrismicConfigPath({
      ignoreCache: args?.ignoreCache
    });
    this._cachedRoot = path.dirname(prismicConfigFilePath);
    return this._cachedRoot;
  }
  async suggestRoot() {
    const suggestedRootPackageJSON = await locateFileUpward("package.json", {
      startDir: this.cwd
    });
    return path.dirname(suggestedRootPackageJSON);
  }
  async suggestPrismicConfigPath() {
    const suggestedRoot = await this.suggestRoot();
    return path.resolve(suggestedRoot, PRISMIC_CONFIG_FILENAME);
  }
  async checkIsTypeScript(args) {
    const root = args?.rootOverride || await this.getRoot();
    const rootTSConfigPath = path.resolve(root, TS_CONFIG_FILENAME);
    return existsSync(rootTSConfigPath);
  }
  async getPrismicConfig() {
    if (this._cachedPrismicConfig) {
      return this._cachedPrismicConfig;
    } else {
      return await this.loadPrismicConfig();
    }
  }
  async writePrismicConfig(args) {
    const configFilePath = args.path || await this.getPrismicConfigPath();
    const config = await format(JSON.stringify(args.config, null, 2), configFilePath);
    await fs.writeFile(configFilePath, config, "utf-8");
    delete this._cachedPrismicConfig;
  }
  async loadPrismicConfig() {
    const configFilePath = await this.getPrismicConfigPath();
    let rawConfig;
    try {
      const contents = await fs.readFile(configFilePath, "utf8");
      rawConfig = JSON.parse(contents);
    } catch (error2) {
      if (error2 instanceof SyntaxError) {
        throw new PrismicError(`Could not parse config file at ${configFilePath}.

Error Message: ${error2.message}`);
      }
    }
    if (!rawConfig) {
      throw new Error("No Prismic config found, please initialize your project with Prismic first.");
    }
    const { value: prismicConfig, error } = decodePrismicConfig(rawConfig);
    if (error) {
      throw new Error(`Invalid Prismic config. ${error.errors.join(", ")}`, {
        cause: { rawConfig }
      });
    }
    this._cachedPrismicConfig = prismicConfig;
    return prismicConfig;
  }
  async getRepositoryName() {
    const prismicConfig = await this.getPrismicConfig();
    return prismicConfig.repositoryName;
  }
  async checkLegacyConfigExists() {
    try {
      await locateFileUpward(SLICEMACHINE_CONFIG_FILENAME, {
        startDir: this.cwd
      });
      return true;
    } catch {
      return false;
    }
  }
  async migrateLegacyConfig() {
    const suggestedRoot = await this.suggestRoot();
    const legacyConfigPath = path.resolve(suggestedRoot, SLICEMACHINE_CONFIG_FILENAME);
    const newConfigPath = path.resolve(suggestedRoot, PRISMIC_CONFIG_FILENAME);
    try {
      await fs.access(legacyConfigPath);
    } catch {
      throw new Error(`Legacy config file ${SLICEMACHINE_CONFIG_FILENAME} not found.`);
    }
    try {
      await fs.access(newConfigPath);
      throw new Error(`Cannot migrate: ${PRISMIC_CONFIG_FILENAME} already exists.`);
    } catch {
    }
    const legacyConfigContent = await fs.readFile(legacyConfigPath, "utf8");
    let rawConfig;
    try {
      rawConfig = JSON.parse(legacyConfigContent);
    } catch (error2) {
      if (error2 instanceof SyntaxError) {
        throw new PrismicError(`Could not parse legacy config file at ${legacyConfigPath}.

Error Message: ${error2.message}`);
      }
      throw error2;
    }
    const { value: prismicConfig, error } = decodePrismicConfig(rawConfig);
    if (error) {
      throw new Error(`Invalid legacy Prismic config. ${error.errors.join(", ")}`, {
        cause: { rawConfig }
      });
    }
    const formattedConfig = await format(JSON.stringify(prismicConfig, null, 2), newConfigPath);
    await fs.writeFile(newConfigPath, formattedConfig, "utf-8");
    await fs.unlink(legacyConfigPath);
    delete this._cachedPrismicConfig;
    delete this._cachedPrismicConfigPath;
    delete this._cachedRoot;
  }
  async initProject(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const log = args?.log || console.log.bind(this);
    const { errors } = await this.pluginSystemRunner.callHook("project:init", {
      log,
      installDependencies: async (args2) => {
        const { execaProcess } = await this.installDependencies({
          dependencies: args2.dependencies,
          dev: args2.dev,
          log
        });
        await execaProcess;
      }
    });
    if (errors.length > 0) {
      throw new PrismicError(`Failed to initialize project: ${errors.join(", ")}`);
    }
  }
  async detectPackageManager(args) {
    const projectRoot = args?.root || await this.getRoot();
    const packageManager = await detect({
      autoInstall: true,
      cwd: projectRoot
    });
    return packageManager || "npm";
  }
  async installDependencies(args) {
    const packageManager = args.packageManager || await this.detectPackageManager();
    const log = args.log || console.log.bind(this);
    const wrappedLogger = (data) => {
      if (data instanceof Buffer) {
        log(data.toString());
      } else if (typeof data === "string") {
        log(data);
      }
    };
    try {
      const projectRoot = await this.getRoot();
      const { execaProcess } = await installDependencies({
        packageManager,
        dependencies: args.dependencies,
        dev: args.dev,
        cwd: projectRoot
      });
      if (process.stdout.isTTY || process.env.NODE_ENV === "test") {
        execaProcess.stdout?.on("data", wrappedLogger);
      }
      execaProcess.stderr?.on("data", wrappedLogger);
      return {
        execaProcess
      };
    } catch (error) {
      if (error instanceof Error && "shortMessage" in error && "stderr" in error) {
        throw new InternalError("Package installation failed", {
          cause: error
        });
      }
      throw error;
    }
  }
}
export {
  ProjectManager
};
//# sourceMappingURL=ProjectManager.js.map
